/*
 * Checkpoint journal for the AI Assistant (notes/ai-assistant-phase1.md §3.5).
 *
 * A checkpoint is the full document state (the history store's tracked
 * stores) taken before an Agent prompt. Records are stored as a keyframe
 * (the whole state) followed by deltas (patches from the previous record),
 * so a chat with many prompts stays small. Restoring rebuilds a complete
 * state rather than replaying patches onto the live project, so a checkpoint
 * can never corrupt a project that changed in the meantime.
 *
 * Pure apart from the injected storage and codec, so it can be unit tested
 * without IndexedDB or the Svelte stores.
 */

import { applyPatches, enablePatches } from "immer";
import { deepEqual, diffPatches } from "../../historyDiff.js";

enablePatches();

const DAY_MS = 24 * 60 * 60 * 1000;

export const JOURNAL_DEFAULTS = {
    /** Records per keyframe chain before a new keyframe is written. */
    maxChainLength: 20,
    /** Delta bytes per chain before a new keyframe is written. */
    maxChainBytes: 2 * 1024 * 1024,
    retentionMs: 30 * DAY_MS,
    maxProjectBytes: 100 * 1024 * 1024,
};

/** Project fields that describe where the project lives, not what it contains. */
export const PRESERVED_PROJECT_FIELDS = [
    "id",
    "teamId",
    "type",
    "username",
    "commit",
    "ownerId",
    "rootDir",
    "folderId",
    "created",
    "media",
    "mediaVersions",
    "activeMediaVersion",
    "useLocalStorage",
    "localPath",
    "selected",
    "compare",
    "mode",
];

/** Stores that are environment, not document: a restore keeps their live value. */
const LIVE_STORES = ["fontState"];

/** Baselines that bring in project content from outside this chat. */
const EXTERNAL_BASELINES = new Set(["project sync", "import project", "project restore"]);
/** Baselines that (re)open a project from its saved file. */
const OPEN_BASELINES = new Set(["load project", "create project"]);

function clone(value) {
    return value === undefined ? undefined : JSON.parse(JSON.stringify(value));
}

/** JSON.stringify with sorted object keys, so equal states give equal strings. */
export function stableStringify(value) {
    if (Array.isArray(value)) {
        return `[${value.map((item) => (item === undefined ? "null" : stableStringify(item))).join(",")}]`;
    }
    if (value !== null && typeof value === "object") {
        const keys = Object.keys(value).filter((key) => value[key] !== undefined).sort();
        return `{${keys.map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(",")}}`;
    }
    return JSON.stringify(value) ?? "null";
}

function fnv1a(text) {
    let hash = 0x811c9dc5;
    for (let i = 0; i < text.length; i++) {
        hash ^= text.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(16).padStart(8, "0");
}

function withoutKeys(object, keys) {
    const result = { ...(object ?? {}) };
    for (const key of keys) {
        delete result[key];
    }
    return result;
}

/**
 * Fingerprint of the document content only: selection, media and storage
 * location are left out so a save/reload of the same content matches.
 * @param {Record<string, unknown>} state
 */
export function fingerprint(state) {
    const groups = Array.isArray(state?.eventGroupState) ? state.eventGroupState : [];
    const text = stableStringify({
        groups: groups.map((group) => withoutKeys(group, ["selected"])),
        metadata: state?.metadataState ?? null,
        project: withoutKeys(state?.projectState, PRESERVED_PROJECT_FIELDS),
        speakers: state?.speakerState ?? null,
        markers: state?.markerState ?? null,
        style: state?.styleState ?? null,
    });
    return `${fnv1a(text)}-${text.length}`;
}

/**
 * Per-store patches that turn `prev` into `next`.
 * @param {Record<string, unknown>} prev
 * @param {Record<string, unknown>} next
 */
export function diffState(prev, next) {
    const delta = {};
    for (const store of new Set([...Object.keys(prev ?? {}), ...Object.keys(next ?? {})])) {
        const patches = diffPatches(prev?.[store], next?.[store]);
        if (patches.length) {
            delta[store] = patches;
        }
    }
    return delta;
}

/**
 * @param {Record<string, unknown>} state
 * @param {Record<string, any[]>} delta
 */
export function applyDelta(state, delta) {
    const next = clone(state);
    for (const [store, patches] of Object.entries(delta ?? {})) {
        next[store] = clone(applyPatches(next[store], patches));
    }
    return next;
}

/**
 * The state a restore writes: the checkpoint's document content, but the
 * project keeps its identity, storage location and media, environment stores
 * keep their live value, and groups that are locked now keep their live
 * content.
 *
 * @param {Record<string, any>} current     live document state
 * @param {Record<string, any>} checkpoint  checkpoint document state
 * @param {{ lockedGroupIds?: Iterable<string> }} [options]
 * @returns {{ state: Record<string, any>, skippedLockedGroups: string[] }}
 */
export function buildRestoredState(current, checkpoint, { lockedGroupIds = [] } = {}) {
    const state = clone(checkpoint);

    const project = { ...(state.projectState ?? {}) };
    for (const field of PRESERVED_PROJECT_FIELDS) {
        if (current?.projectState && field in current.projectState) {
            project[field] = clone(current.projectState[field]);
        } else {
            delete project[field];
        }
    }
    state.projectState = project;

    for (const store of LIVE_STORES) {
        if (current && store in current) {
            state[store] = clone(current[store]);
        }
    }

    const skippedLockedGroups = [];
    const groups = Array.isArray(state.eventGroupState) ? state.eventGroupState : [];
    const liveGroups = Array.isArray(current?.eventGroupState) ? current.eventGroupState : [];
    for (const id of lockedGroupIds) {
        const live = liveGroups.find((group) => group?.id === id);
        if (!live) continue;
        const index = groups.findIndex((group) => group?.id === id);
        const restored = index === -1 ? undefined : groups[index];
        if (!deepEqual(withoutKeys(restored, ["selected"]), withoutKeys(live, ["selected"]))) {
            skippedLockedGroups.push(live.name ?? id);
        }
        if (index === -1) {
            groups.push(clone(live));
        } else {
            groups[index] = clone(live);
        }
    }
    state.eventGroupState = groups;

    const selected = project.selected;
    if (selected !== false && selected !== undefined && selected !== null && !groups[selected]) {
        project.selected = groups.length ? 0 : false;
    }

    return { state, skippedLockedGroups };
}

/**
 * What a restore would change, for the confirmation dialog.
 * @param {Record<string, any>} current
 * @param {Record<string, any>} restored  output of buildRestoredState
 */
export function summarizeRestore(current, restored) {
    const before = new Map((current?.eventGroupState ?? []).map((group) => [group.id, group]));
    const after = new Map((restored?.eventGroupState ?? []).map((group) => [group.id, group]));
    let events = 0;
    const groups = [];

    for (const id of new Set([...before.keys(), ...after.keys()])) {
        const a = before.get(id);
        const b = after.get(id);
        if (!a || !b) {
            events += (a ?? b).events?.length ?? 0;
            groups.push((a ?? b).name ?? id);
            continue;
        }
        const aEvents = new Map((a.events ?? []).map((event) => [event.id, event]));
        const bEvents = new Map((b.events ?? []).map((event) => [event.id, event]));
        let changed = 0;
        for (const eventId of new Set([...aEvents.keys(), ...bEvents.keys()])) {
            if (!deepEqual(aEvents.get(eventId), bEvents.get(eventId))) changed++;
        }
        const settingsChanged = !deepEqual(
            withoutKeys(a, ["events", "selected"]),
            withoutKeys(b, ["events", "selected"]),
        );
        if (changed || settingsChanged) {
            events += changed;
            groups.push(b.name ?? a.name ?? id);
        }
    }

    const projectChanged = !deepEqual(
        withoutKeys(current?.projectState, PRESERVED_PROJECT_FIELDS),
        withoutKeys(restored?.projectState, PRESERVED_PROJECT_FIELDS),
    );
    const otherChanged = ["metadataState", "speakerState", "markerState", "styleState"].some(
        (store) => !deepEqual(current?.[store], restored?.[store]),
    );

    return {
        events,
        groups,
        projectChanged: projectChanged || otherChanged,
        unchanged: events === 0 && groups.length === 0 && !projectChanged && !otherChanged,
    };
}

/**
 * @typedef {object} JournalStorage
 * @property {(record: object) => Promise<void>} putCheckpoint
 * @property {(id: string) => Promise<object|undefined>} getCheckpoint
 * @property {(projectId: string) => Promise<object[]>} listCheckpoints  records without `data` are fine
 * @property {(ids: string[]) => Promise<void>} deleteCheckpoints
 * @property {(projectId: string) => Promise<object|undefined>} getMeta
 * @property {(meta: object) => Promise<void>} putMeta
 */

/**
 * @param {object} options
 * @param {JournalStorage} options.storage
 * @param {(text: string) => Promise<Uint8Array|string>} options.encode
 * @param {(data: Uint8Array|string) => Promise<string>} options.decode
 * @param {() => string} options.newId
 * @param {() => number} [options.now]
 * @param {Partial<typeof JOURNAL_DEFAULTS>} [options.limits]
 */
export function createJournal({ storage, encode, decode, newId, now = Date.now, limits = {} }) {
    const config = { ...JOURNAL_DEFAULTS, ...limits };
    /** projectId -> { lastId, lastState, chainId, chainLength, chainBytes } */
    const sessions = new Map();
    let queue = Promise.resolve();

    /** Run writes one at a time so each delta is based on the record before it. */
    function enqueue(task) {
        const run = queue.then(task, task);
        queue = run.catch(() => {});
        return run;
    }

    async function updateMeta(projectId, patch) {
        const meta = (await storage.getMeta(projectId)) ?? { projectId };
        const next = { ...meta, ...patch, projectId };
        await storage.putMeta(next);
        return next;
    }

    async function prune(projectId) {
        const records = await storage.listCheckpoints(projectId);
        const current = sessions.get(projectId)?.chainId;
        const chains = new Map();
        for (const record of records) {
            const chain = chains.get(record.chainId) ?? { id: record.chainId, ids: [], bytes: 0, newest: 0 };
            chain.ids.push(record.id);
            chain.bytes += record.bytes ?? 0;
            chain.newest = Math.max(chain.newest, record.createdAt ?? 0);
            chains.set(record.chainId, chain);
        }
        const ordered = [...chains.values()].sort((a, b) => a.newest - b.newest);
        const cutoff = now() - config.retentionMs;
        let total = ordered.reduce((sum, chain) => sum + chain.bytes, 0);
        const doomed = [];
        for (const chain of ordered) {
            if (chain.id === current) continue;
            if (chain.newest < cutoff || total > config.maxProjectBytes) {
                doomed.push(...chain.ids);
                total -= chain.bytes;
            }
        }
        if (doomed.length) {
            await storage.deleteCheckpoints(doomed);
        }
        return doomed.length;
    }

    /**
     * Store a checkpoint of `state`. The state must already be a plain copy
     * (the caller captures it synchronously, before any edit of the turn).
     * @param {{ id?: string, projectId: string, chatId?: string|null, messageId?: string|null, reason?: string, state: Record<string, unknown> }} params
     * @returns {Promise<{ id: string, fingerprint: string }>}
     */
    function createCheckpoint({ id = newId(), projectId, chatId = null, messageId = null, reason = "prompt", state }) {
        return enqueue(async () => {
            const session = sessions.get(projectId);
            const createdAt = now();
            const fp = fingerprint(state);
            const asDelta =
                session &&
                session.chainLength < config.maxChainLength &&
                session.chainBytes < config.maxChainBytes;

            const payload = asDelta ? diffState(session.lastState, state) : state;
            const data = await encode(JSON.stringify(payload));
            const bytes = typeof data === "string" ? data.length : data.byteLength;
            const record = {
                id,
                projectId,
                chatId,
                messageId,
                reason,
                createdAt,
                fingerprint: fp,
                type: asDelta ? "delta" : "keyframe",
                baseId: asDelta ? session.lastId : null,
                chainId: asDelta ? session.chainId : id,
                bytes,
                data,
            };

            try {
                await storage.putCheckpoint(record);
            } catch (error) {
                // The next checkpoint must not be a delta on a record that was never written.
                sessions.delete(projectId);
                throw error;
            }

            sessions.set(projectId, {
                lastId: id,
                lastState: clone(state),
                chainId: record.chainId,
                chainLength: asDelta ? session.chainLength + 1 : 1,
                chainBytes: asDelta ? session.chainBytes + bytes : 0,
            });
            await updateMeta(projectId, { fingerprint: fp, updatedAt: createdAt });
            await prune(projectId).catch((error) => console.warn("[checkpoints] prune failed", error));
            return { id, fingerprint: fp };
        });
    }

    /**
     * Rebuild the document state of a checkpoint, or null when it (or a record
     * it depends on) has been pruned.
     * @param {string} id
     */
    function loadCheckpoint(id) {
        return enqueue(async () => {
            for (const session of sessions.values()) {
                if (session.lastId === id) return clone(session.lastState);
            }
            const chain = [];
            let record = await storage.getCheckpoint(id);
            while (record) {
                chain.unshift(record);
                if (record.type === "keyframe") break;
                record = record.baseId ? await storage.getCheckpoint(record.baseId) : undefined;
            }
            if (!chain.length || chain[0].type !== "keyframe") {
                return null;
            }
            let state = JSON.parse(await decode(chain[0].data));
            for (const delta of chain.slice(1)) {
                state = applyDelta(state, JSON.parse(await decode(delta.data)));
            }
            return state;
        });
    }

    /**
     * Track project loads and saves (historyStore baselines).
     * - "save project": the saved content becomes the known fingerprint.
     * - sync/import/snapshot restore: content came from outside the chat, so
     *   older checkpoints are marked as predating outside changes.
     * - load/create: the same, but only if the opened file differs from the
     *   last content this journal knew about.
     * Any baseline except a save starts a new keyframe chain.
     * `getState` is only called for projects that have checkpoints.
     * @param {{ projectId: string, name: string, getState: () => Record<string, unknown> }} params
     */
    function noteBaseline({ projectId, name, getState }) {
        return enqueue(async () => {
            if (name !== "save project") {
                sessions.delete(projectId);
            }
            const meta = await storage.getMeta(projectId);
            if (!meta) {
                // This project has no checkpoints: nothing to track.
                return null;
            }
            const fp = fingerprint(getState());
            const patch = { fingerprint: fp, updatedAt: now() };
            const changedOutside =
                EXTERNAL_BASELINES.has(name) ||
                (OPEN_BASELINES.has(name) && meta.fingerprint && meta.fingerprint !== fp);
            if (changedOutside) {
                patch.detachedAt = now();
            }
            return updateMeta(projectId, patch);
        });
    }

    /** After a restore the live content is known again. */
    function noteRestored({ projectId, state }) {
        return enqueue(() => updateMeta(projectId, { fingerprint: fingerprint(state), updatedAt: now() }));
    }

    function getMeta(projectId) {
        return enqueue(() => storage.getMeta(projectId));
    }

    function listCheckpoints(projectId) {
        return enqueue(() => storage.listCheckpoints(projectId));
    }

    return {
        createCheckpoint,
        loadCheckpoint,
        noteBaseline,
        noteRestored,
        getMeta,
        listCheckpoints,
        prune: (projectId) => enqueue(() => prune(projectId)),
        /** Forget in-memory chains (tests, project close). */
        resetSessions: () => sessions.clear(),
    };
}

/** Whether a checkpoint was taken before the project was changed outside the chat. */
export function isDetached(record, meta) {
    return Boolean(meta?.detachedAt && record && record.createdAt < meta.detachedAt);
}
